import { format } from "date-fns/esm";

import { unixTimestampToMs } from "utils/time";

import { ChangeHistoryRecord } from "./gql";
import { ChangeHistoryDateGroup, ChangeHistoryEntry, InputChange, InputSnapshot } from "./types";

export const diffInputs = (
  previous: InputSnapshot[],
  current: InputSnapshot[]
): InputChange[] => {
  const changes: InputChange[] = [];

  const previousMap = new Map(
    previous.filter((i) => !i.secret).map((i) => [i.id, i])
  );
  const currentIds = new Set(current.map((i) => i.id));

  for (const input of current) {
    if (input.secret) {
      const prev = previous.find((i) => i.id === input.id);
      if (!prev) {
        changes.push({
          fieldId: input.id,
          fieldName: input.id,
          previousValue: "(not set)",
          currentValue: input.checksum,
          secret: true,
        });
      } else if (prev.checksum !== input.checksum) {
        changes.push({
          fieldId: input.id,
          fieldName: input.id,
          previousValue: prev.checksum,
          currentValue: input.checksum,
          secret: true,
        });
      }
      continue;
    }

    const prev = previousMap.get(input.id);

    if (!prev) {
      if (!input.value) continue;
      changes.push({
        fieldId: input.id,
        fieldName: input.id,
        previousValue: "(not set)",
        currentValue: input.value,
        secret: false,
      });
      continue;
    }

    if (prev.value !== input.value) {
      changes.push({
        fieldId: input.id,
        fieldName: input.id,
        previousValue: prev.value || "(not set)",
        currentValue: input.value || "(not set)",
        secret: false,
      });
    }
  }

  for (const prev of previous) {
    if (currentIds.has(prev.id)) continue;

    changes.push({
      fieldId: prev.id,
      fieldName: prev.id,
      previousValue: prev.secret ? prev.checksum : (prev.value ?? "(not set)"),
      currentValue: "(removed)",
      secret: prev.secret,
    });
  }

  return changes;
};

const formatGroupDateLabel = (timestamp: number): string => {
  return format(unixTimestampToMs(timestamp), "MMM do, yyyy");
};

const computeChangeHistoryGroups = (records: ChangeHistoryRecord[]): ChangeHistoryDateGroup[] => {
  const entries: ChangeHistoryEntry[] = [];

  for (const record of records) {
    const previousInputs = record.previousInputs ?? [];
    const inputChanges = diffInputs(previousInputs, record.inputs);
    const hasVersionChange =
      record.previousVersion !== null &&
      record.previousVersion !== undefined &&
      record.previousVersion.id !== record.version.id;

    entries.push({
      id: record.id,
      timestamp: record.createdAt,
      targetVersionNumber: record.version.number ?? "",
      versionChange: hasVersionChange
        ? {
            from: record.previousVersion!.number ?? "",
            to: record.version.number ?? "",
            previousVersionId: record.previousVersion!.id,
            previousBody: record.previousVersion!.rawTemplate,
            currentBody: record.version.rawTemplate,
          }
        : undefined,
      inputChanges,
      inputs: record.inputs,
    });
  }

  const groupMap = new Map<string, ChangeHistoryEntry[]>();
  for (const entry of entries) {
    const dayKey = format(unixTimestampToMs(entry.timestamp), "yyyy-MM-dd");
    const group = groupMap.get(dayKey) ?? [];
    group.push(entry);
    groupMap.set(dayKey, group);
  }

  return Array.from(groupMap.values()).map((groupEntries) => ({
    dateKey: groupEntries[0].timestamp,
    dateLabel: formatGroupDateLabel(groupEntries[0].timestamp),
    entries: groupEntries,
  }));
};

export default computeChangeHistoryGroups;

export const getLatestEntryId = (groups: ChangeHistoryDateGroup[]): string | undefined => {
  const allEntries = groups.flatMap((g) => g.entries);
  if (allEntries.length === 0) return undefined;
  return allEntries.reduce((latest, entry) =>
    entry.timestamp > latest.timestamp ? entry : latest
  ).id;
};
